import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RealtimeConnection } from './realtime-connection';
import { RealtimeEnvelope } from './realtime-envelope';
import { REALTIME_CONFIG, REALTIME_TOKEN_PROVIDER } from './realtime.config';

interface EchoReply {
  message: string;
  userId: string;
  receivedAt: string;
}

/**
 * Test panel for the review. Two things:
 *  - Echo: client invokes a hub method, server pushes back to the caller (proves both directions).
 *  - Publish to me: a REST call makes the server publish to this user's group via the publisher,
 *    and it arrives here on the shared "Receive" handler (proves group-addressed delivery).
 */
@Component({
  selector: 'lib-realtime-diagnostics',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="rt">
      <header>
        <h2>Realtime connection</h2>
        <span class="pill" [class]="'pill--' + connection.state()">{{ connection.state() }}</span>
      </header>

      <dl>
        <dt>Connection id</dt>
        <dd>{{ connection.connectionId() ?? '—' }}</dd>
      </dl>

      <p class="hint">
        Confirm the transport server-side: the backend logs <code>transport=WebSockets</code> on connect. Anything else
        means the handshake fell back to long polling.
      </p>

      <div class="row">
        <input type="text" [(ngModel)]="message" placeholder="type a message" (keyup.enter)="send()" />
        <button type="button" (click)="send()" [disabled]="!connection.isConnected()">Echo</button>
        <button type="button" (click)="publishToMe()" [disabled]="!connection.isConnected() || sending()">
          Publish to me
        </button>
        <button type="button" (click)="connect()" [disabled]="connection.isConnected()">Connect</button>
        <button type="button" (click)="disconnect()" [disabled]="!connection.isConnected()">Disconnect</button>
      </div>

      @if (status(); as s) {
        <p class="status" [class.status--error]="s.error">{{ s.text }}</p>
      }

      <h3>Echoes (caller round-trip)</h3>
      @if (echoes().length === 0) {
        <p class="empty">Nothing yet.</p>
      } @else {
        <ul>
          @for (e of echoes(); track e.receivedAt) {
            <li>
              <code>{{ e.message }}</code> <time>{{ e.receivedAt }}</time>
            </li>
          }
        </ul>
      }

      <h3>Published events (via "Receive")</h3>
      @if (events().length === 0) {
        <p class="empty">Nothing yet. Press "Publish to me".</p>
      } @else {
        <ul>
          @for (ev of events(); track ev.eventId) {
            <li>
              <code>{{ ev.type }}</code> <span>{{ asText(ev.payload) }}</span> <time>{{ ev.occurredAt }}</time>
            </li>
          }
        </ul>
      }
    </section>
  `,
  styles: [
    `
      .rt {
        font-family: ui-monospace, Consolas, monospace;
        font-size: 13px;
        padding: 1rem;
      }
      header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-bottom: 0.75rem;
      }
      h2 {
        font-size: 15px;
        margin: 0;
      }
      h3 {
        font-size: 13px;
        margin: 1rem 0 0.35rem;
      }
      .pill {
        text-transform: uppercase;
        letter-spacing: 0.08em;
        font-size: 10px;
        padding: 2px 8px;
        border-radius: 2px;
        border: 1px solid currentColor;
      }
      .pill--connected {
        color: #2e6b4f;
      }
      .pill--connecting,
      .pill--reconnecting {
        color: #8a5a0c;
      }
      .pill--offline {
        color: #a32e24;
      }
      dl {
        display: grid;
        grid-template-columns: max-content 1fr;
        gap: 0.25rem 1rem;
        margin: 0 0 0.75rem;
      }
      dt {
        opacity: 0.6;
      }
      dd {
        margin: 0;
      }
      .hint {
        opacity: 0.7;
        max-width: 60ch;
        line-height: 1.5;
      }
      .row {
        display: flex;
        gap: 0.5rem;
        margin: 0.75rem 0;
        flex-wrap: wrap;
      }
      input {
        font: inherit;
        padding: 0.25rem 0.5rem;
      }
      .status {
        margin: 0.25rem 0 0.5rem;
      }
      .status--error {
        color: #a32e24;
      }
      ul {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }
      li {
        display: flex;
        gap: 0.75rem;
        align-items: baseline;
      }
      time,
      .empty {
        opacity: 0.6;
      }
    `
  ]
})
export class RealtimeDiagnosticsComponent implements OnInit {
  readonly connection = inject(RealtimeConnection);
  readonly #destroyRef = inject(DestroyRef);
  readonly #config = inject(REALTIME_CONFIG);
  readonly #tokens = inject(REALTIME_TOKEN_PROVIDER);

  message = '';
  readonly sending = signal(false);
  readonly status = signal<{ text: string; error: boolean } | null>(null);
  readonly echoes = signal<EchoReply[]>([]);
  readonly events = signal<RealtimeEnvelope[]>([]);

  ngOnInit(): void {
    void this.connection.start();

    this.connection
      .on<EchoReply>('ReceiveEcho')
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(reply => this.echoes.update(list => [reply, ...list].slice(0, 50)));

    // Every published event, whatever feature sent it, arrives on this one handler.
    this.connection.envelopes$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(envelope => this.events.update(list => [envelope, ...list].slice(0, 50)));
  }

  send(): void {
    const text = this.message.trim();
    if (!text) return;
    void this.connection.echo(text);
    this.message = '';
  }

  async publishToMe(): Promise<void> {
    this.sending.set(true);
    this.status.set(null);
    try {
      const url = new URL('/api/v2/realtime/diagnostics/publish-to-me', this.#config.hubUrl).href;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.#tokens.getToken() ?? ''}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: this.message.trim() || null })
      });
      this.status.set(
        res.ok
          ? { text: 'Publish requested. Watch "Published events" below.', error: false }
          : { text: `Publish failed: HTTP ${res.status}`, error: true }
      );
    } catch (e) {
      this.status.set({ text: `Publish errored: ${e}`, error: true });
    } finally {
      this.sending.set(false);
    }
  }

  connect(): void {
    void this.connection.start();
  }

  disconnect(): void {
    void this.connection.stop();
  }

  asText(payload: unknown): string {
    return typeof payload === 'string' ? payload : JSON.stringify(payload);
  }
}
